import * as React from 'react'
import { toast } from 'sonner'

interface UseFileHandlingProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  chatState: any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  chatActions: any
}

export const useFileHandling = ({ chatState, chatActions: _chatActions }: UseFileHandlingProps) => {
  const fileInputRef = React.useRef<HTMLInputElement>(null)

  // Check if selected file is an image
  const isImageSelected = React.useMemo(
    () => !!(chatState.selectedFile && chatState.selectedFile.type.startsWith('image/')),
    [chatState.selectedFile]
  )

  // Create preview URL for image files
  const selectedFilePreviewUrl = React.useMemo(() => {
    if (!isImageSelected || !chatState.selectedFile) return undefined
    return URL.createObjectURL(chatState.selectedFile)
  }, [isImageSelected, chatState.selectedFile])

  // Cleanup preview URL on unmount
  React.useEffect(() => {
    return () => {
      if (selectedFilePreviewUrl) URL.revokeObjectURL(selectedFilePreviewUrl)
    }
  }, [selectedFilePreviewUrl])

  // Reset file input when selectedFile is cleared
  React.useEffect(() => {
    if (!chatState.selectedFile && fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [chatState.selectedFile])

  // Handle file selection
  const handleFileSelect = React.useCallback(
    (file?: File) => {
      if (file) {
        // Handle direct file selection (from paste)
        const isVideo = file.type.startsWith('video/')
        const maxSize = isVideo ? 100 * 1024 * 1024 : 10 * 1024 * 1024
        if (file.size > maxSize) {
          toast.error(isVideo ? 'Video size must be less than 100MB' : 'File size must be less than 10MB')
          return
        }

        const allowedTypes = [
          'image/',
          'video/',
          'audio/',
          'application/pdf',
          'application/msword',
          'application/vnd.openxmlformats-officedocument',
          'text/',
          'application/zip',
          'application/x-rar-compressed',
          'application/json',
        ]

        const isAllowedType = allowedTypes.some(type => file.type.startsWith(type))
        if (!isAllowedType) {
          toast.error('File type not supported')
          return
        }

        chatState.setSelectedFile(file)
      } else {
        // Handle file input click (from button)
        fileInputRef.current?.click()
      }
    },
    [chatState]
  )

  // Handle file change
  const handleFileChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (file) {
        const isVideo = file.type.startsWith('video/')
        const maxSize = isVideo ? 100 * 1024 * 1024 : 10 * 1024 * 1024
        if (file.size > maxSize) {
          toast.error(isVideo ? 'Video size must be less than 100MB' : 'File size must be less than 10MB')
          return
        }

        const allowedTypes = [
          'image/',
          'video/',
          'audio/',
          'application/pdf',
          'application/msword',
          'application/vnd.openxmlformats-officedocument',
          'text/',
          'application/zip',
          'application/x-rar-compressed',
          'application/json',
        ]

        const isAllowedType = allowedTypes.some(type => file.type.startsWith(type))
        if (!isAllowedType) {
          toast.error('File type not supported')
          return
        }

        chatState.setSelectedFile(file)
      }
    },
    [chatState]
  )

  // Handle file removal
  const handleRemoveFile = React.useCallback(() => {
    chatState.setSelectedFile(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [chatState])

  return {
    fileInputRef,
    isImageSelected,
    selectedFilePreviewUrl,
    handleFileSelect,
    handleFileChange,
    handleRemoveFile,
    isUploadingFile: chatState.isUploadingFile,
  }
}
